import React, { useState, useEffect, useMemo, useRef } from "react";
import { generateWithholdingPDF } from "../../utils/pdf/WithholdingPDF/generateWithholdingPDF";
import { formatDecimal, formatMoney, formatFiscalPeriod, formatDate, formatText } from "../../utils/formatters";

// (2026-05-15) Rediseño Sirius. Panel/header/footer/botones migrados al
// patrón estándar y secciones en tarjetas `Item`. La lógica (viewMode,
// PDF, gating de rol, tabla de documentos afectados) se conserva idéntica.
const ModalWithholding = ({ data, onClose }) => {
  const [viewMode, setViewMode] = useState("detalle");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    if (!data) {
      setViewMode("detalle");
      setPdfUrl(null);
    }
  }, [data]);

  const handleViewPDF = async () => {
    try {
      setLoadingPdf(true);

      let url;
      url = await generateWithholdingPDF(data.id, "view");
      setPdfUrl(url);
      setViewMode("pdf");

    } catch (err) {
      console.error(err);
    } finally {
      setLoadingPdf(false);
    }
  };

  if (!data) return null;
  const authData = localStorage.getItem("authData");
  const rol = authData ? JSON.parse(authData)["rol"] : "";

  const Item = ({ label, children, full }) => (
    <div className={full ? "w-full" : "w-full sm:w-1/2 lg:w-1/3"}>
      <div className="bg-slate-50/70 rounded-xl border border-slate-200/70 p-4 h-full">
        <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-1.5">{label}</h3>
        <div className="text-sm font-medium text-ink-900 break-words">{children}</div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-center">
      {/* (2026-05-18) Altura FIJA (h-[90vh]): antes solo max-h sin alto
          definido -> en modo PDF el contenedor flex-1 colapsaba (el iframe
          no aporta alto intrínseco) y el visor quedaba en una franja. Con
          alto fijo el área del PDF llena el espacio (mismo patrón que la
          modal de Clientes). Un poco más ancha para mejor lectura del PDF. */}
      <div className="bg-white rounded-2xl shadow-sirius-lg w-full max-w-[1200px] h-[90vh] max-h-[90vh] flex flex-col overflow-hidden">

        {/* HEADER */}
        <div className="px-6 md:px-8 py-5 border-b border-slate-200 bg-white">
          <div className="flex justify-between items-center gap-3">
            <h6 className="text-lg md:text-xl font-display font-bold text-ink-900">
              Retención Nº {data.numero_comprobante}
            </h6>

            <div className="flex gap-2">
              <button
                className="px-4 py-1.5 bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white text-sm font-semibold rounded-lg transition"
                onClick={handleViewPDF}
              >
                Ver PDF
              </button>

              {viewMode === "pdf" && (
                <button
                  className="px-4 py-1.5 bg-slate-200 hover:bg-slate-300 text-ink-700 text-sm font-semibold rounded-lg transition"
                  onClick={() => setViewMode("detalle")}
                >
                  Volver
                </button>
              )}
            </div>
          </div>
        </div>

        {viewMode === "detalle" && (
          <div className="flex-1 min-h-0 overflow-y-auto px-6 md:px-8 py-6">
            {rol == 'admin' && (
              <>
                <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-400 mb-3">
                  Datos del Emisor
                </h3>
                <div className="flex flex-wrap gap-3 mb-2">
                  <Item label="R.I.F">{formatText(data.empresa?.rif)}</Item>
                  <Item label="Razón social">{formatText(data.empresa?.nombre_empresa)}</Item>
                </div>
              </>
            )}

            <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-400 mt-5 mb-3">
              Datos del Sujeto Retenido
            </h3>
            <div className="flex flex-wrap gap-3">
              {/* (2026-05-17) FIND-4: en retenciones de tercero el nested
                  sujeto_retenido viene null; el dato vive en el snapshot
                  plano. Fallback para no mostrar Nombre/RIF vacíos. */}
              <Item label="Nombre">{formatText(data.sujeto_retenido?.nombre || data.sujeto_retenido_nombre)}</Item>
              <Item label="RIF">{formatText(data.sujeto_retenido?.rif || data.sujeto_retenido_rif)}</Item>
            </div>

            {/* (2026-05-18) Datos del Tercero: solo cuando la retención
                proviene de una factura por cuenta de terceros
                (doc_tercero / tercero_id). El nested `tercero` lo expone
                ComprobanteRetencionSchema (id, nombre_empresa, rif). */}
            {(data.doc_tercero || data.tercero_id || data.tercero) && (
              <>
                <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-400 mt-5 mb-3">
                  Datos del Tercero (Afiliada)
                </h3>
                <div className="flex flex-wrap gap-3">
                  <Item label="Razón Social">{formatText(data.tercero?.nombre_empresa)}</Item>
                  <Item label="R.I.F">{formatText(data.tercero?.rif)}</Item>
                </div>
              </>
            )}

            <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-400 mt-5 mb-3">
              Información
            </h3>
            <div className="flex flex-wrap gap-3">
              <Item label="Período Fiscal">{formatFiscalPeriod(data.periodo_fiscal)}</Item>
              <Item label="Fecha Emisión">{formatDate(data.fecha_emision)}</Item>
              <Item label="Fecha Entrega">{data.fecha_entrega ? formatDate(data.fecha_entrega) : ""}</Item>
              <Item label="Estatus">{data.estatus}</Item>
              <Item label="Estatus Seniat">{data.estatus_seniat}</Item>
            </div>

            <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-400 mt-5 mb-3">
              Montos
            </h3>
            <div className="flex flex-wrap gap-3">
              <Item label="Base Imponible Total">{formatMoney(data.monto_base_total)}</Item>
              <Item label="Monto Retenido Total">{formatMoney(data.monto_retenido_total)}</Item>
            </div>

            <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-400 mt-5 mb-3">
              Usuario
            </h3>
            <div className="flex flex-wrap gap-3">
              <Item label="Operador">{data.usuario?.nombre}</Item>
              <Item label="Email">{data.usuario?.email}</Item>
            </div>

            {/* tabla de items */}
            <h3 className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-400 mt-7 mb-3">
              Documentos Afectados
            </h3>

            <div className="overflow-x-auto rounded-xl border border-slate-200/70">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-ink-500 text-[11px] uppercase tracking-[0.08em]">
                    <th className="p-2.5 text-left">Tipo</th>
                    <th className="p-2.5 text-left">Número</th>
                    <th className="p-2.5 text-left">Número Control</th>
                    <th className="p-2.5 text-left">Fecha</th>
                    <th className="p-2.5 text-right">Base Imponible</th>
                    <th className="p-2.5 text-center">Alicuota</th>
                    <th className="p-2.5 text-center">Retención %</th>
                    <th className="p-2.5 text-right">Monto Retenido</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items?.length ? (
                    data.items.map((i) => {
                      let tipoDoc = "FACTURA";
                      switch (i.tipo_documento_afectado) {
                        case "FC":
                          tipoDoc = "FACTURA";
                          break;
                        case "FD":
                          tipoDoc = "NOTA DE DÉBITO";
                          break;
                        case "NC":
                          tipoDoc = "NOTA DE CRÉDITO";
                          break;
                        default:
                          tipoDoc = "FACTURA";
                      }

                      return (
                        <tr key={i.id} className="border-t border-slate-100 text-ink-900">
                          <td className="p-2.5">{tipoDoc}</td>
                          <td className="p-2.5">{i.factura_afectada_numero}</td>
                          <td className="p-2.5">{i.factura_afectada_control}</td>
                          <td className="p-2.5">{formatDate(i.factura_afectada_fecha)}</td>
                          <td className="p-2.5 text-right">{formatDecimal(i.monto_base_imponible)}</td>
                          <td className="p-2.5 text-center">{(i.alicuota_iva != null ? formatDecimal(i.alicuota_iva) : '')}</td>
                          <td className="p-2.5 text-center">{i.tipo_retencion?.descripcion}</td>
                          <td className="p-2.5 text-right">{formatDecimal(i.monto_retenido)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center p-3 text-ink-500">
                        No hay documentos asociados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {viewMode === "pdf" && (
          <div className="flex-1 min-h-0">
            {loadingPdf ? (
              <div className="text-center mt-10 text-ink-500">Generando PDF...</div>
            ) : (
              <iframe
                src={`${pdfUrl}#toolbar=` + (rol == 'visor' ? '0':'1')}
                width="100%"
                height="100%"
                style={{ border: "none" }}
                title="PDF Viewer"
              />
            )}
          </div>
        )}

        {/* FOOTER */}
        <div className="shrink-0 w-full flex justify-center py-4 border-t border-slate-200 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-twilight-indigo-600 hover:bg-twilight-indigo-500 text-white font-semibold rounded-xl transition"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalWithholding;
